import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Form, Input, Modal, Row, Select } from 'antd';
import { useForm } from 'antd/es/form/Form';
import debounce from 'lodash/debounce';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { useQuery } from '@apollo/client';
import { GET_AREAS_LIST, GET_GOVERNORATES_LIST } from '../../graphql/queries';

import { NewLocation, saveSubLocations } from '../../redux/actions/initiatives';

import NewLocationMap from './NewLocationMap';

const { Option } = Select;

const NewLocationModal = ({ show, setShow, refetchData }) => {
  const [newLocationForm] = useForm();

  const Close = () => {
    setShow(false);
  };

  const { loading: governoratesListLoading, data: governoratesList } = useQuery(
    GET_GOVERNORATES_LIST,
  );

  const { loading: areasListLoading, data: areasList } =
    useQuery(GET_AREAS_LIST);

  const dispatch = useDispatch();

  const [errorMessage, setErrorMessage] = useState(null);
  const [isChecked] = useState(true);
  const [governorate, setGovernorate] = useState('');
  const [locationFields, setlocationFields] = useState({
    lat: null,
    lng: null,
    radius: 0,
  });
  const [inputFields] = useState([
    {
      name_en: '',
      name_ar: '',
    },
  ]);

  const onFinish = async values => {
    const data = {
      ...values,
      is_main_location: true,
      latitude: locationFields.lat,
      longitude: locationFields.lng,
    };
    const response = await dispatch(NewLocation(data));
    // if an error appeared while adding the main location.
    if (typeof response === 'string' && response.includes('Error'))
      setErrorMessage(response);
    // if no error proceed with adding the sub-locations.
    else {
      setErrorMessage(null);
      const subLocationData = {
        main_location: response.id,
        is_active: response.is_active,
        main_location_address: response.main_location_address,
      };
      await dispatch(saveSubLocations(inputFields, subLocationData));
      // close the modal and refetch data.
      setShow(false);
      refetchData();
      // reset form values.
      newLocationForm.resetFields();
    }
  };

  const onLocationClick = ({ lng, lat }) => {
    setlocationFields(prev => ({
      ...prev,
      lat,
      lng,
    }));
  };

  const debouncedSetRadius = debounce(value => {
    setlocationFields(prev => ({
      ...prev,
      radius: Number(value),
    }));
  }, 10);

  return (
    <Modal
      title="New Location Form"
      footer={null}
      open={show}
      onCancel={Close}
      width="1200px"
    >
      {isChecked && (
        <NewLocationMap
          apiKey={process.env.REACT_APP_TOMTOM_API_KEY}
          onLocationClick={onLocationClick}
          radius={locationFields.radius}
        />
      )}

      <Form form={newLocationForm} onFinish={onFinish}>
        {errorMessage && <p className="text-error">{errorMessage}</p>}

        <Row gutter={[8, 8]}>
          <Col md={12} xs={24}>
            <Form.Item
              name="name_en"
              label="Name in English"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col md={12} xs={24}>
            <Form.Item
              name="name_ar"
              label="Name in Arabic"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input className="arabic-field" />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              name="is_active"
              label={
                <span>
                  Is Active&nbsp;&nbsp;
                  <InfoCircleOutlined
                    title="whether or not to show the location in the application form"
                    style={{
                      verticalAlign: 'middle',
                    }}
                  />
                </span>
              }
              valuePropName="checked"
              initialValue={true}
            >
              <Checkbox />
            </Form.Item>
          </Col>

          {/* <Col xs={24}>
            <Form.Item
              name="main_location_address"
              label="Add address information per main location"
              valuePropName="checked"
              initialValue={false}
            >
              <Checkbox
                onChange={event => setIsChecked(event.target.checked)}
              />
            </Form.Item>
          </Col> */}

          {isChecked && (
            <>
              <Col md={12} xs={24}>
                <Form.Item
                  name="governorate"
                  label="Governorate"
                  initialValue=""
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Select onChange={value => setGovernorate(value)}>
                    <Option key="" value="">
                      -----
                    </Option>
                    ,
                    {!governoratesListLoading &&
                      governoratesList?.governorates.edges.map(obj => (
                        <Option key={obj.node.id} value={obj.node.name}>
                          {obj.node.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col md={12} xs={24}>
                <Form.Item
                  name="area"
                  label="Area"
                  initialValue=""
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Select>
                    <Option key="" value="">
                      -----
                    </Option>
                    ,
                    {governorate &&
                      !areasListLoading &&
                      areasList?.areas.edges
                        .filter(
                          obj => obj.node.governorate.name === governorate,
                        )
                        .map(obj => (
                          <Option key={obj.node.id} value={obj.node.name}>
                            {obj.node.name}
                          </Option>
                        ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col md={8} xs={24}>
                <Form.Item
                  label="Latitude"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Input
                    value={locationFields.lat}
                    onChange={e => {
                      setlocationFields({
                        ...locationFields,
                        lat: e.currentTarget.value,
                      });
                    }}
                  />
                </Form.Item>
              </Col>
              <Col md={8} xs={24}>
                <Form.Item
                  label="longitude"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Input
                    value={locationFields.lng}
                    onChange={e => {
                      setlocationFields({
                        ...locationFields,
                        lng: e.currentTarget.value,
                      });
                    }}
                  />
                </Form.Item>
              </Col>

              <Col md={8} xs={24}>
                <Form.Item
                  label="Radius (in meters)"
                  rules={[{ required: true }]}
                >
                  <Input
                    type="number"
                    value={locationFields.radius}
                    onChange={e => {
                      debouncedSetRadius(e.currentTarget.value);
                    }}
                  />
                </Form.Item>
              </Col>
            </>
          )}

          {/* <Col xs={24}>
            <Form.List name="sub-locations-form" initialValue={inputFields}>
              {(fields, { add, remove }) => {
                return (
                  <Card
                    title={
                      <>
                        Sub-Locations
                        <PlusCircleOutlined
                          className="mx-3"
                          onClick={() => {
                            addFields();
                            add();
                          }}
                        />
                      </>
                    }
                    className="mb-5"
                  >
                    {fields.map((field, index) => (
                      <React.Fragment key={index}>
                        <Row gutter={[8, 8]} key={field.key}>
                          <Col md={12} xs={24}>
                            <Form.Item
                              name={[index, 'name_en']}
                              label="Name in English"
                              rules={[
                                {
                                  required: true,
                                  message: "'name_en' is required",
                                },
                              ]}
                            >
                              <Input
                                name="name_en"
                                onChange={event =>
                                  onChange(
                                    index,
                                    event.target.name,
                                    event.target.value,
                                  )
                                }
                              />
                            </Form.Item>
                          </Col>

                          <Col md={12} xs={24}>
                            <Form.Item
                              name={[index, 'name_ar']}
                              label="Name in Arabic"
                              rules={[
                                {
                                  required: true,
                                  message: "'name_ar' is required",
                                },
                              ]}
                            >
                              <Input
                                className="arabic-field"
                                name="name_ar"
                                onChange={event =>
                                  onChange(
                                    index,
                                    event.target.name,
                                    event.target.value,
                                  )
                                }
                              />
                            </Form.Item>
                          </Col>

                          {!isChecked && (
                            <>
                              <Col md={12} xs={24}>
                                <Form.Item
                                  name={[index, 'governorate']}
                                  label="Governorate"
                                  initialValue=""
                                  rules={[
                                    {
                                      required: true,
                                      message: "'governorate' is required",
                                    },
                                  ]}
                                >
                                  <Select
                                    onChange={value =>
                                      onChange(index, 'governorate', value)
                                    }
                                  >
                                    <Option key="" value="">
                                      -----
                                    </Option>
                                    ,
                                    {!governoratesListLoading &&
                                      governoratesList?.governorates.edges.map(
                                        obj => (
                                          <Option
                                            key={obj.node.id}
                                            value={obj.node.name}
                                          >
                                            {obj.node.name}
                                          </Option>
                                        ),
                                      )}
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col md={12} xs={24}>
                                <Form.Item
                                  name={[index, 'area']}
                                  label="Area"
                                  initialValue=""
                                  rules={[
                                    {
                                      required: true,
                                      message: "'area' is required",
                                    },
                                  ]}
                                >
                                  <Select
                                    onChange={value =>
                                      onChange(index, 'area', value)
                                    }
                                  >
                                    <Option key="" value="">
                                      -----
                                    </Option>
                                    ,
                                    {inputFields[index]['governorate'] &&
                                      !areasListLoading &&
                                      areasList?.areas.edges
                                        .filter(
                                          obj =>
                                            obj.node.governorate.name ===
                                            inputFields[index]['governorate'],
                                        )
                                        .map(obj => (
                                          <Option
                                            key={obj.node.id}
                                            value={obj.node.name}
                                          >
                                            {obj.node.name}
                                          </Option>
                                        ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                            </>
                          )}

                          <Col md={4} xs={24}>
                            <Button
                              onClick={() => {
                                removeFields(index);
                                remove(field.name);
                              }}
                            >
                              Remove
                            </Button>
                          </Col>
                        </Row>

                        {fields.length > 1 && index !== fields.length - 1 && (
                          <Divider />
                        )}
                      </React.Fragment>
                    ))}
                  </Card>
                );
              }}
            </Form.List>
          </Col> */}

          <Col xs={24}>
            <Button htmlType="submit">Submit</Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default NewLocationModal;
